import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Participant, SourceDataFormStatus, Trial } from "@/api/dtos";
import Multiselect, { IMultiselectProps } from "@/components/forms/inputs/Multiselect";
import Text, { ITextProps } from "@/components/forms/inputs/Text";
import CoordinatingGroupsContext from "@/contexts/data/CoordinatingGroupsContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import InstitutionsContext from "@/contexts/data/InstitutionsContext";
import MasterGroupsContext from "@/contexts/data/MasterGroupsContext";
import TrialContext from "@/contexts/data/TrialContext";
import { faExclamationCircle } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, AlertTitle, Box, ListItemIcon, ListItemText, darken, lighten, styled } from "@mui/material";
import { DateTime } from "luxon";
import { ComponentType, FC, memo, useCallback, useContext, useMemo } from "react";
import StatusList, { IStatusListCreateAdditional, IStatusListCreateConfigureLink, IStatusListCreateLabel, IStatusListCreateLink, IStatusListIsDisabled, IStatusListSearch, IStatusListStatsComponentProps, StatusListItemIcon, StatusListItemText, StatusListProps } from "./StatusList";
import { DefaultSortableFields, IStatusListSortable } from "./StatusListSort";
import DefaultStatComponents, { getNoteCount, INoteLinkComponentProps, NoteLink, StatusListItemButtonTooltip, StatusListItemIconBox, StatusListItemTooltip, StatusListItemTooltipStats, StatusListStatsBox } from './StatusListStats';
import useForm from "@/hooks/form/source-data/useForm";
import sourceDataFormStatusToText from "@/utilities/configuration/sourceDataFormStatusToText";
import sourceDataFormStatusToColor from "@/utilities/configuration/sourceDataFormStatusToColor";
import sourceDataFormStatusToIcon from "@/utilities/configuration/sourceDataFormStatusToIcon";
import { Link as RouteLink } from "react-router-dom";
import ChosenThemeSizeContext from "@/contexts/styles/ChosenThemeSizeContext";

export type ParticipantSourceDataFormStatusListItemType = NonNullable<ReturnType<typeof useForm>>

export interface IParticipantSourceDataFormStatusListAdditional {
    NoteLinkComponent?: ComponentType<INoteLinkComponentProps<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional>>
}

const ParticipantSourceDataNoteLinkComponent = memo(({
    itemData,
    status,
    type
}: INoteLinkComponentProps<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional>) => {
    const value = getNoteCount(itemData, type, status);

    if (value === undefined) {
        return (
            <>
                &mdash;
            </>
        )
    }
    
    let baseLink = `/${itemData?.data?.trial?.oid}${itemData?.data?.institution ? `/institutions/${itemData.data.institution.code}` : ''}/participants/${itemData?.data?.participant?.studyNumber}/events/${itemData?.data?.studyEventOid}/${itemData?.data?.studyEventRepeat}/forms/${itemData?.data.caseReportFormOid}/notes-and-queries`;

    if (type && status) {
        baseLink += `?type=${type}&status=${status}`;
    }
    else if (type) {
        baseLink += `?type=${type}`;
    }
    else if (status) {
        baseLink += `?status=${status}`;
    }

    return (
        <NoteLink 
            //@ts-ignore
            component={RouteLink} 
            to={baseLink}
        >
            {value}
        </NoteLink>
    )
})

export interface IParticipantSourceDataFormStatusListProps extends StatusListProps<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional> {
    trial?: Trial;
    institution?: Institution;
    participant?: Participant;
}


const EnrolmentBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(15),
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? darken(theme.palette.primary.main,0.5) : lighten(theme.palette.primary.main, 0.5) 
}));

const InstBox = styled(Box)(({ theme }) => ({ 
    display: 'flex',
    alignItems: 'center',
    width: theme.spacing(15),
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? darken(theme.palette.secondary.main,0.5) : lighten(theme.palette.secondary.main, 0.5)
}));

const createParticipantSourceDataFormStatusLabel: IStatusListCreateLabel<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional> = (itemData) => {
    return (
        <>
            <StatusListItemIcon>
                <FontAwesomeIcon fixedWidth size="lg" icon={itemData.data.icon} />
            </StatusListItemIcon>
            <StatusListItemText 
                primary={itemData.data.displayName} 
            />
        </>
    )
};

const createParticipantSourceDataEventLink: IStatusListCreateLink<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional> = (itemData) => {
    return itemData.data.preview ? itemData.data.previewLink : itemData.data.dataLink;
};

const createParticipantSourceDataEventConfigurationLink: IStatusListCreateConfigureLink<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional> = (itemData) => {
    return itemData.data.configurationLink;
};

const isParticipantSourceDataEventDisabled: IStatusListIsDisabled<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional> = (itemData) => {
    return !itemData.data.status || itemData.data.status === ConfigurationStatus.Disabled;
};

const ParticipantSourceDataEventStatsComponent = ({
    itemData, 
    theme
}: IStatusListStatsComponentProps<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional>) => {
    return (
        <StatusListStatsBox>
            <DefaultStatComponents.DisabledIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReadOnlyIndicatorStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.VerificationPriorityIndicatorStats itemData={itemData} theme={theme} />
            <StatusListItemButtonTooltip
                placement="left"
                title={
                    <>
                        <strong>Status</strong>
                        <div>
                            {sourceDataFormStatusToText(itemData.data.sourceDataForm?.status)} 
                        </div>
                    </>
                }
            >
                <StatusListItemIconBox>
                    <Box 
                        sx={{ 
                            color: sourceDataFormStatusToColor(itemData.data.sourceDataForm?.status)
                        }}
                    >
                        <FontAwesomeIcon 
                            icon={sourceDataFormStatusToIcon(itemData.data.sourceDataForm?.status)} 
                        />
                    </Box>
                </StatusListItemIconBox>
            </StatusListItemButtonTooltip>
            <StatusListItemTooltip
                placement="left"
                title={
                    <>
                        <strong>Dates</strong>
                        {
                            itemData.data.sourceDataForm?.startDate && (
                                <StatusListItemTooltipStats>
                                    <span>
                                        Start Date
                                    </span>
                                    <span>
                                        {DateTime.fromISO(itemData.data.sourceDataForm.startDate).toFormat('dd/MM/yyyy')}
                                    </span>
                                </StatusListItemTooltipStats>
                            )
                        }
                        {
                            itemData.data.sourceDataForm?.completeDate && (
                                <StatusListItemTooltipStats>
                                    <span>
                                        Complete Date
                                    </span>
                                    <span>
                                        {DateTime.fromISO(itemData.data.sourceDataForm.completeDate).toFormat('dd/MM/yyyy')}
                                    </span>
                                </StatusListItemTooltipStats>
                            )
                        }
                        {
                            itemData.data.sourceDataForm?.updateDate && (
                                <StatusListItemTooltipStats>
                                    <span>
                                        Update Date
                                    </span>
                                    <span>
                                        {DateTime.fromISO(itemData.data.sourceDataForm.updateDate).toFormat('dd/MM/yyyy')}
                                    </span>
                                </StatusListItemTooltipStats>
                            )
                        }
                        {
                            !itemData.data.sourceDataForm?.startDate && (
                                <Box>
                                    Not Started
                                </Box>
                            )
                        }
                    </>
                }
            >
                <EnrolmentBox>
                    {
                        itemData.data.sourceDataForm?.completeDate && (
                            DateTime.fromISO(itemData.data.sourceDataForm.completeDate).toFormat('dd/MM/yyyy')
                        )
                    }
                    {
                        !itemData.data.sourceDataForm?.completeDate && (
                            'Not Complete'
                        )
                    }
                </EnrolmentBox>
            </StatusListItemTooltip>
            <DefaultStatComponents.NoteStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.ReviewStats itemData={itemData} theme={theme} />
            <DefaultStatComponents.VerificationStats itemData={itemData} theme={theme} />
        </StatusListStatsBox>
    )
};


const SortableFields: IStatusListSortable<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional>[] = [
    {
        name: 'Form Order',
        sort: (a, b) => a.data.eventFormMetadata!.order! > b.data.eventFormMetadata!.order! ? 1 : -1
    },
    {
        name: 'Form Name',
        sort: (a, b) => a.data.displayName! > b.data.displayName! ? 1 : -1
    },
    {
        name: 'Start Date',
        sort: (a, b) => a.data.sourceDataForm?.startDate! > b.data.sourceDataForm?.startDate! ? 1 : -1
    },
    {
        name: 'Complete Date',
        sort: (a, b) => a.data.sourceDataForm?.completeDate! > b.data.sourceDataForm?.completeDate! ? 1 : -1
    },
    {
        name: 'Update Date',
        sort: (a, b) => a.data.sourceDataForm?.updateDate! > b.data.sourceDataForm?.updateDate! ? 1 : -1
    },
    DefaultSortableFields.priority,
    DefaultSortableFields.crfCompletion,
    DefaultSortableFields.openQueries,
    DefaultSortableFields.reviewCompletion,
    DefaultSortableFields.verificationCompletion,
    DefaultSortableFields.status
]

const DefaultSearchData = { statuses: [ConfigurationStatus.Enabled, ConfigurationStatus.ReadOnly] };

const createParticipantSourceDataEventAdditional: IStatusListCreateAdditional<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional> = (itemData) => {
    return {
        NoteLinkComponent: ParticipantSourceDataNoteLinkComponent
    }
};

const ParticipantSourceDataFormStatusList: FC<IParticipantSourceDataFormStatusListProps> = ({
    trial, 
    institution,
    participant,
    ...statusListProps
}) => {    

    const searchableFields: IStatusListSearch<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional>[] = useMemo(() => {
        const output: IStatusListSearch<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional, any, any>[] = [
            {
                key: 'studyNumber',
                component: Text,
                componentProps: {
                    margin: 'normal'
                } as ITextProps,
                label: 'Study Number',
                searchFn: (item, value) => value ? item.data.participant?.studyNumber?.includes(value) ?? false : false
            } as IStatusListSearch<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional, string>
        ]

        output.push({
            key: 'statuses',
            component: Multiselect,
            componentProps: {
                options: [
                    {
                        id: ConfigurationStatus.Disabled,
                        name: 'Disabled',
                        description: `The participant is disabled in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.ReadOnly,
                        name: 'Read Only',
                        description: `The participant is read only in the Source Data Verification system.`
                    },
                    {
                        id: ConfigurationStatus.Enabled,
                        name: 'Enabled',
                        description: `The participant is enabled in the Source Data Verification system.`
                    }
                ],
                FormControlProps: {
                    margin: 'normal'
                }
                
            } as IMultiselectProps,
            label: 'Statuses',
            searchFn: (item, value) => value && value.length > 0 ?
                item.data.status ?
                    value.includes(item.data.status) ?? false : 
                    false :
                true
        } as IStatusListSearch<ParticipantSourceDataFormStatusListItemType, IParticipantSourceDataFormStatusListAdditional, ConfigurationStatus[]>)

        return output;
    }, []);
    

    const themeSize = useContext(ChosenThemeSizeContext);

    const height = useMemo(() => {
        switch (themeSize) {
            case 'extra-small':
                return 25;
            case 'small':
                return 37;
            case 'medium':
                return 49;
            case 'large':
                return 61;
            case 'extra-large':
                return 73;
        }
    }, [themeSize])

    return (
        <StatusList 
            {...statusListProps}
            createLabel={createParticipantSourceDataFormStatusLabel}
            createLink={createParticipantSourceDataEventLink}
            createConfigureLink={createParticipantSourceDataEventConfigurationLink}
            isDisabled={isParticipantSourceDataEventDisabled}
            createAdditional={createParticipantSourceDataEventAdditional}
            StatsComponent={ParticipantSourceDataEventStatsComponent}
            sortKey="participant-source-data-forms-sort"
            sortableFields={SortableFields}
            noRecords={
                <Alert
                    severity="warning"
                >
                    <AlertTitle>No Forms</AlertTitle>
                    No forms were found for the participant {participant?.studyNumber} on the {trial?.name}{institution ? ` at ${institution.name}` : ''}.
                </Alert> 
            }
            searchKey={`participant-source-data-forms-search-${trial?.oid}-${institution?.code ?? 'All'}`}
            fixedItemHeight={height}
            defaultSearchData={DefaultSearchData}
        />
    )
};

export default ParticipantSourceDataFormStatusList;